/* eslint-disable max-lines */
import { ConversationContextCard, LoadMoreButton, ContentCard } from '@content-search/components';
import type { ContentCardItem, ContentSearchItem } from '@content-search/domain';
import { useConversationContext } from '@content-search/hooks';
import { ErrorBanner, Illustration, IllustrationLayout, IllustrationType } from '@core/components';
import { IconButton, ChevronLeft, IconSize, IconButtonSize, ButtonType, vars, Shimmer, Badge, Card } from '@soteria/design-system-ve';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';

import * as conversationContextStyles from './ConversationContextCard.css';
import { convertToContentCardItem, hasValidMetadata } from './SlidingContextView/helpers';
import * as styles from './SlidingContextView.css';

interface SlidingContextViewProps {
  communityId: string;
  subjectId: string;
  topicName: string;
  metadata: Record<string, unknown>;
  onBack: () => void;
  selectedMessage?: ContentCardItem;
  mediaServiceUrl?: string;
}

// eslint-disable-next-line complexity
export function SlidingContextView({ communityId, subjectId, topicName, metadata, onBack, selectedMessage, mediaServiceUrl }: SlidingContextViewProps) {
  const { fetchConversationContext, fetchMoreContext, isLoading, errors } = useConversationContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [targetMessage, setTargetMessage] = useState<ContentSearchItem | null>(null);
  const [conversationMessages, setConversationMessages] = useState<ContentSearchItem[]>([]);
  const [loadingDirection, setLoadingDirection] = useState<'above' | 'below' | null>(null);
  const [canLoadMore, setCanLoadMore] = useState({ above: true, below: true });
  const [selectedMessageForCard, setSelectedMessageForCard] = useState<ContentSearchItem | null>(null);
  const [hasScrolledToTarget, setHasScrolledToTarget] = useState(false);

  // Memoize metadata to prevent unnecessary re-renders
  const memoizedMetadata = useMemo(() => metadata, [metadata]);

  // Check if metadata is empty or has no meaningful values
  const isMetadataValid = useMemo(() => hasValidMetadata(memoizedMetadata), [memoizedMetadata]);

  // Fetch conversation context on component mount
  useEffect(() => {
    // If there's no valid metadata, don't make the API call at all
    if (!isMetadataValid) {
      // Set empty state immediately without making any API calls
      setTargetMessage(null);
      setConversationMessages([]);
      setCanLoadMore({ above: false, below: false });

      return;
    }

    // eslint-disable-next-line complexity
    const loadConversationContext = async () => {
      const context = await fetchConversationContext({
        communityId,
        subjectId,
        topicName,
        metadata: memoizedMetadata,
        messagesBefore: 5,
        messagesAfter: 5,
      });

      if (context) {
        // Organize messages properly: preceding + selected + following
        let selectedAsContentSearchItem: ContentSearchItem | null = null;

        if (selectedMessage) {
          // Convert selectedMessage to ContentSearchItem format
          // Use subjectId if available, otherwise fall back to id
          const messageSubjectId = selectedMessage.subjectId || selectedMessage.id;

          selectedAsContentSearchItem = {
            id: selectedMessage.id,
            subjectId: messageSubjectId,
            timestamp: selectedMessage.timestamp,
            eventType: selectedMessage.eventType || 'Classification',
            createdAt: selectedMessage.timestamp || new Date().toISOString(),
            communityId: '', // ContentCardItem doesn't have communityId
            topicName: selectedMessage.topicName || selectedMessage.topic || '',
            contentType: selectedMessage.contentType || selectedMessage.type || 'audio',
            isSampled: selectedMessage.isSampled || false,
            classificationResults: {
              transcript: selectedMessage.text || '',
              // Map other fields as needed
            },
            contentMetadata: {
              subjectId: messageSubjectId,
              audioFileUrl: selectedMessage.audioFileUrl || '',
              contentUrl: selectedMessage.contentUrl || '',
              timestamp: selectedMessage.timestamp || '',
              metadata: selectedMessage.metadata || {},
              subjectOwner: {
                type: selectedMessage.subjectOwner.type || '',
                id: selectedMessage.subjectOwner.userId || '',
                additionalInfo: {
                  username: selectedMessage.subjectOwner.username || '',
                },
              },
              customValues: selectedMessage.customFields || {},
            },
          };

          // Set the target message to the selected message for highlighting
          setTargetMessage(selectedAsContentSearchItem);
        } else {
          // Fallback: try to find target message using API response
          const combinedMessages = [...context.precedingMessages, ...context.followingMessages];
          const actualTargetMessage =
            context.targetFound && context.targetSubjectId
              ? combinedMessages.find(message => (message.subjectId || message.id) === context.targetSubjectId) || null
              : null;

          setTargetMessage(actualTargetMessage);
        }

        // Build chronological conversation array: preceding (reversed) + selected + following
        // Note: precedingMessages come in reverse chronological order from API (0th is closest to target)
        // so we need to reverse them to display in chronological order
        const reversedPrecedingMessages = [...context.precedingMessages].reverse();
        const chronologicalMessages: ContentSearchItem[] = [
          ...reversedPrecedingMessages,
          ...(selectedAsContentSearchItem ? [selectedAsContentSearchItem] : []),
          ...context.followingMessages,
        ];

        setConversationMessages(chronologicalMessages);

        // Update load more availability based on results
        setCanLoadMore({
          above: context.precedingMessages.length >= 5,
          below: context.followingMessages.length >= 5,
        });
      }
    };

    loadConversationContext();
  }, [communityId, subjectId, topicName, memoizedMetadata, isMetadataValid, fetchConversationContext, selectedMessage]);

  // Scroll to selected message only once on initial load
  useEffect(() => {
    if (targetMessage && conversationMessages.length > 0 && !hasScrolledToTarget) {
      // Wait for the DOM to update, then scroll to the selected message using page scroll
      const timeoutId = setTimeout(() => {
        const selectedElement = document.querySelector(`[data-message-id="${targetMessage.id}"]`);

        if (selectedElement) {
          const htmlElement = selectedElement as HTMLElement;

          // Scroll the selected element into view using the page scroll
          htmlElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });

          // Mark that we've scrolled to the target
          setHasScrolledToTarget(true);
        }
      }, 300); // Wait for DOM updates and animations

      return () => clearTimeout(timeoutId);
    }
  }, [targetMessage, conversationMessages, hasScrolledToTarget]);

  // Determine if a message should be aligned left or right based on user ID
  // Use the original selectedMessage for alignment, not the currently clicked targetMessage
  const getMessageAlignment = (message: ContentSearchItem): 'left' | 'right' => {
    // Use selectedMessage (original selection) for alignment reference, fallback to targetMessage
    const referenceMessage = selectedMessage || (targetMessage ? convertToContentCardItem(targetMessage) : null);

    if (referenceMessage) {
      // Convert the current message to ContentCardItem to access the proper user identification
      const messageCardItem = convertToContentCardItem(message);

      // Compare the actual user IDs from subjectOwner with the original selected message
      return messageCardItem.subjectOwner.userId === referenceMessage.subjectOwner.userId ? 'right' : 'left';
    }

    return 'left';
  };

  // Handle loading more messages above
  // eslint-disable-next-line complexity
  const handleLoadMoreAbove = useCallback(async () => {
    const earliestMessage = conversationMessages[0];

    const messageId = (earliestMessage?.contentMetadata?.subjectId as string) || earliestMessage?.subjectId || earliestMessage?.id;

    if (!conversationMessages.length || loadingDirection || !isMetadataValid || !earliestMessage || !messageId || typeof messageId !== 'string') {
      return;
    }

    setLoadingDirection('above');

    try {
      // Use the earliest message (chronologically) for the API call
      const context = await fetchMoreContext({
        communityId,
        subjectId: messageId,
        topicName,
        metadata: memoizedMetadata,
        messagesBefore: 5,
        messagesAfter: 0,
      });

      if (context && context.precedingMessages.length > 0) {
        // Reverse the new preceding messages and prepend them to maintain chronological order
        const reversedNewMessages = [...context.precedingMessages].reverse();

        setConversationMessages(prev => [...reversedNewMessages, ...prev]);
        setCanLoadMore(prev => ({ ...prev, above: context.precedingMessages.length >= 5 }));
      } else {
        setCanLoadMore(prev => ({ ...prev, above: false }));
      }
    } catch {
      // Error is handled by the hook's error state
    } finally {
      setLoadingDirection(null);
    }
  }, [communityId, conversationMessages, topicName, memoizedMetadata, isMetadataValid, fetchMoreContext, loadingDirection]);

  // Handle loading more messages below
  // eslint-disable-next-line complexity
  const handleLoadMoreBelow = useCallback(async () => {
    const bottomMostMessage = conversationMessages[conversationMessages.length - 1];

    if (!conversationMessages.length || loadingDirection || !isMetadataValid) {
      return;
    }

    const messageId = (bottomMostMessage?.contentMetadata?.subjectId as string) || bottomMostMessage?.subjectId || bottomMostMessage?.id;

    setLoadingDirection('below');

    try {
      if (!messageId || typeof messageId !== 'string') {
        return;
      }

      const context = await fetchMoreContext({
        communityId,
        subjectId: messageId,
        topicName,
        metadata: memoizedMetadata,
        messagesBefore: 0,
        messagesAfter: 5,
      });

      if (context && context.followingMessages.length > 0) {
        setConversationMessages(prev => [...prev, ...context.followingMessages]);
        setCanLoadMore(prev => ({ ...prev, below: context.followingMessages.length >= 5 }));
      } else {
        setCanLoadMore(prev => ({ ...prev, below: false }));
      }
    } catch {
      // Error is handled by the hook's error state
    } finally {
      setLoadingDirection(null);
    }
  }, [communityId, conversationMessages, topicName, memoizedMetadata, isMetadataValid, fetchMoreContext, loadingDirection]);

  // Handle clicking on a conversation context card to update the selected message card
  const handleMessageClick = useCallback((message: ContentSearchItem) => {
    setSelectedMessageForCard(message);
  }, []);

  return (
    <div>
      {/* Context Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: vars.space[5],
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: vars.space[3] }}>
          <IconButton buttonSize={IconButtonSize.Medium} buttonType={ButtonType.Secondary} icon={ChevronLeft} iconSize={IconSize.XXSmall} onClick={onBack} />
          <h2 style={{ margin: 0 }}>Conversation Context</h2>
        </div>
      </div>

      {/* Applied Filters Section - matching audit page format exactly */}
      {isMetadataValid && (
        <div style={{ marginBottom: vars.space[5] }}>
          <div className={styles.appliedFiltersHeader}>Conversation Details</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            {Object.entries(memoizedMetadata).map(([key, value]) => {
              // Only show meaningful metadata values
              if (value === null || value === undefined || value === '') return null;

              const displayValue = String(value);
              const isNoneValue = displayValue.toLowerCase() === 'none';

              return (
                <div key={`metadata-${key}-${value}`} className={styles.badgeGroup}>
                  <span className={styles.badgeLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}:</span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {isNoneValue ? <span className={styles.emptyBadgeText}>None</span> : <Badge text={displayValue} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Banner - only show for actual errors (conversation not found errors are filtered out in the hook) */}
      {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error fetching conversation context:" />}

      {/* Conversation Context Container */}
      <div>
        {/* Show conversation-style shimmers during initial loading - only if we have valid metadata */}
        {isLoading && conversationMessages.length === 0 && isMetadataValid ? (
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: vars.space[4] }}>
              {/* Left-aligned message shimmer */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                <div style={{ width: '60%' }}>
                  <Shimmer style={{ height: '80px', width: '100%', borderRadius: '8px' }} />
                </div>
              </div>

              {/* Right-aligned message shimmer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                <div style={{ width: '60%' }}>
                  <Shimmer style={{ height: '100px', width: '100%', borderRadius: '8px' }} />
                </div>
              </div>

              {/* Another left-aligned message shimmer with different height */}
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                <div style={{ width: '60%' }}>
                  <Shimmer style={{ height: '90px', width: '100%', borderRadius: '8px' }} />
                </div>
              </div>
            </div>
          </Card>
        ) : conversationMessages.length === 0 ? (
          /* No messages state - show empty state illustration without load more buttons */
          <Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '300px',
                padding: vars.space[6],
              }}
            >
              <Illustration
                description="No messages could be found for this conversation context."
                layout={IllustrationLayout.HORIZONTAL}
                title="No conversation found"
                type={IllustrationType.EMPTY}
              />
            </div>
          </Card>
        ) : (
          /* Conversation card wrapper */
          <Card className={styles.conversationContextCardWrapper}>
            <div ref={scrollContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: vars.space[2] }}>
              {/* Load more button above or shimmer for loading above - only show if there are messages */}
              {canLoadMore.above && !loadingDirection && <LoadMoreButton direction="up" isLoading={false} onClick={handleLoadMoreAbove} />}
              {loadingDirection === 'above' && (
                <div style={{ padding: '16px' }}>
                  {/* Right-aligned message shimmer */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                    <div style={{ width: '60%' }}>
                      <Shimmer style={{ height: '80px', width: '100%', borderRadius: '8px' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* All messages rendered as conversation context cards only */}
              {conversationMessages.map(message => {
                const alignment = getMessageAlignment(message);
                const messageCardItem = convertToContentCardItem(message);

                // Check if this message is the original selected message (always highlighted)
                const messageSubjectId = messageCardItem.subjectId || messageCardItem.id;
                const originalSelectedId = selectedMessage?.subjectId || selectedMessage?.id;
                const isOriginalSelectedMessage = selectedMessage ? messageSubjectId === originalSelectedId : false;

                return (
                  <div key={message.subjectId}>
                    {/* Selected message label - only show above the originally selected message */}
                    {isOriginalSelectedMessage && <div className={conversationContextStyles.selectedMessageLabel}>SELECTED MESSAGE</div>}

                    {/* Message content with conditional highlighting */}
                    <div className={isOriginalSelectedMessage ? conversationContextStyles.originalSelectedMessage : ''}>
                      <ConversationContextCard
                        alignment={alignment}
                        isOriginalSelection={false}
                        isSelected={false}
                        item={messageCardItem}
                        mediaServiceUrl={mediaServiceUrl}
                        onClick={() => handleMessageClick(message)}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Load more button below or shimmer for loading below - only show if there are messages */}
              {loadingDirection === 'below' && (
                <div style={{ padding: '16px' }}>
                  {/* Right-aligned message shimmer */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                    <div style={{ width: '60%' }}>
                      <Shimmer style={{ height: '80px', width: '100%', borderRadius: '8px' }} />
                    </div>
                  </div>
                </div>
              )}
              {canLoadMore.below && !loadingDirection && <LoadMoreButton direction="down" isLoading={false} onClick={handleLoadMoreBelow} />}
            </div>
          </Card>
        )}
      </div>

      {/* Selected Message Card Section */}
      {selectedMessageForCard && (
        <div style={{ marginTop: vars.space[5] }}>
          <h3 style={{ margin: `0 0 ${vars.space[3]} 0`, fontSize: vars.fontSizes.h4, fontWeight: vars.fontWeights.semibold }}>Message Details</h3>
          <ContentCard item={convertToContentCardItem(selectedMessageForCard)} mediaServiceUrl={mediaServiceUrl} />
        </div>
      )}
    </div>
  );
}
